import { debounce } from "lodash-es";
import React, { ReactElement, useCallback, useEffect, useState } from "react";

import { InputAdornment } from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import { useDispatch, useSelector } from "../../store";
import { ReduxFormProps } from "../../types/ReduxForm";
import { NoViewIcon, ViewIcon } from "../icons";
import StyledTextField from "../styled/TextField";

export type ReduxTextProps = TextFieldProps &
  ReduxFormProps & {
    disabledText?: string;
    validator?: (val: string) => string;
  };

export const ReduxHiddenDataField = ({
  getter,
  setter,
  errorHandler,
  path,
  disabledText,
  disabled,
  defaultValue = "",
  validator = () => "",
  required = false,
  ...props
}: ReduxTextProps): ReactElement => {
  const dispatch = useDispatch();
  const formValue = useSelector(getter(path, defaultValue)) as string;
  const [inputValue, setInputValue] = useState<string>(formValue);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showData, setShowData] = useState(false);

  useEffect(() => {
    if (errorHandler) {
      dispatch(errorHandler({ path, error: errorMessage }));
    }
  }, [dispatch, errorMessage, path]);

  const delayedOnSave = useCallback(
    debounce((val) => {
      dispatch(setter({ path, value: val }));
      setErrorMessage(validator(val));
    }, 150),
    [setter, path],
  );

  const onChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(evt.target.value);
    delayedOnSave(evt.target.value);
  };

  const onBlur = (): void => {
    dispatch(setter({ path, value: inputValue }));
    setErrorMessage(validator(inputValue));
  };

  const emptyButRequired = required && !inputValue;

  if (disabled && !!disabledText) {
    return (
      <Tooltip title={disabledText} placement={"bottom-start"}>
        <span>
          <StyledTextField
            fullWidth
            {...props}
            type="password"
            autoComplete="one-time-code"
            error={!!errorMessage.length || emptyButRequired}
            helperText={
              errorMessage ||
              (emptyButRequired ? "This field is required!" : "")
            }
            disabled={disabled}
            placeholder={"Disabled"}
            value={inputValue}
            onChange={onChange}
          />
        </span>
      </Tooltip>
    );
  }

  return (
    <StyledTextField
      {...props}
      fullWidth
      autoComplete="one-time-code"
      type={showData ? "text" : "password"}
      error={!!errorMessage.length || emptyButRequired}
      helperText={
        errorMessage || (emptyButRequired ? "This field is required!" : "")
      }
      disabled={disabled}
      value={inputValue}
      onChange={onChange}
      onBlur={onBlur}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" id="">
            {showData && (
              <NoViewIcon
                sx={{ cursor: "pointer" }}
                onClick={() => setShowData(false)}
              />
            )}
            {!showData && (
              <ViewIcon
                sx={{ cursor: "pointer" }}
                onClick={() => setShowData(true)}
              />
            )}
          </InputAdornment>
        ),
      }}
    />
  );
};
